import { useState, memo, useRef, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Star, Download, Palette, Sparkles, Store, ChevronRight, ImageIcon, Globe, MapPin, ShoppingBag, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useSearchHistory } from "@/contexts/SearchHistoryContext";
import { getProductThumbnail } from "@/lib/imageUtils";
import { buildProductInquiryMessage, buildWhatsappUrl } from "@/lib/whatsappUtils";

interface PrintAreaMockup {
  placement?: string;
  url?: string;
}

interface PrintAreas {
  mockups?: PrintAreaMockup[];
}

interface ProductCardProps {
  id: string;
  title: string;
  price: number;
  images: string[];
  isDigital?: boolean;
  isCustomizable?: boolean;
  isSample?: boolean;
  isPrintOnDemand?: boolean;
  shippingCostUs?: number | null;
  printAreas?: PrintAreas | null;
  rating?: number;
  reviewCount?: number;
  discountPrice?: number | null;
  discountStartDate?: string | null;
  discountEndDate?: string | null;
  createdAt?: string | null;
  tags?: string[];
  sellerId?: string;
  storeSlug?: string | null;
  storeName?: string;
  storeLogo?: string | null;
  storeWhatsapp?: string | null;
  variant?: "default" | "compact";
  eager?: boolean;
  fetchPriority?: "high" | "low" | "auto";
  visibilityBadge?: string | null;
  visibilityBadgeColor?: string | null;
  categoryMetadata?: { disponibilidad?: string; zona_entrega?: string } | null;
  themeColor?: string;
}

const isNewProduct = (createdAt: string | null | undefined): boolean => {
  if (!createdAt) return false;
  const createdDate = new Date(createdAt);
  const now = new Date();
  const daysDiff = (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
  return daysDiff <= 7;
};

const isDiscountActive = (
  discountPrice: number | null | undefined,
  startDate: string | null | undefined,
  endDate: string | null | undefined
): boolean => {
  if (!discountPrice) return false;
  const now = new Date();
  if (startDate && new Date(startDate) > now) return false;
  if (endDate && new Date(endDate) < now) return false;
  return true;
};

const ProductCard = memo(({ 
  id,
  title, 
  price, 
  images, 
  isDigital, 
  isCustomizable,
  isSample = false,
  isPrintOnDemand = false,
  shippingCostUs,
  printAreas,
  rating = 0,
  reviewCount = 0,
  discountPrice,
  discountStartDate,
  discountEndDate,
  createdAt,
  tags,
  sellerId,
  storeSlug,
  storeName,
  storeLogo,
  storeWhatsapp,
  variant = "default",
  eager = false,
  fetchPriority,
  visibilityBadge,
  visibilityBadgeColor,
  categoryMetadata,
  themeColor,
}: ProductCardProps) => {
  const isCompact = variant === "compact";
  const navigate = useNavigate();
  const storeLink = `/tienda/${storeSlug || sellerId}`;
  
  const searchContext = useSearchHistory();
  
  const contextRefs = useRef({ search: searchContext });
  
  useEffect(() => {
    contextRefs.current = { search: searchContext };
  });
  
  // Image loading state - rely on native lazy loading, no observers
  const [imageLoaded, setImageLoaded] = useState(eager);
  const [imageError, setImageError] = useState(false);
  const [buyHover, setBuyHover] = useState(false);
  const [waHover, setWaHover] = useState(false);


  // Get optimized image URL - safely extract first image, fallback to POD mockup (filter invalid URLs)
  const podMockupUrl = isPrintOnDemand 
    ? printAreas?.mockups?.find(m => !!m.url)?.url 
    : null;
  const rawImageUrl = images?.[0] || podMockupUrl || null;
  const imageUrl = rawImageUrl ? getProductThumbnail(rawImageUrl) : null;
  
  const hasActiveDiscount = isDiscountActive(discountPrice, discountStartDate, discountEndDate);
  const discountPercentage = hasActiveDiscount && discountPrice
    ? Math.round(((price - discountPrice) / price) * 100)
    : 0;
  const isNew = isNewProduct(createdAt);

  // Don't allow quick add for customizable or sample products
  const canQuickAdd = !isCustomizable && !isSample;

  const handleTagClick = useCallback((e: React.MouseEvent, tag: string) => {
    e.preventDefault();
    e.stopPropagation();
    contextRefs.current.search.addSearch(tag.toLowerCase());
    navigate(`/productos?tag=${encodeURIComponent(tag)}`);
  }, [navigate]);

  return (
    <Link to={`/productos/${id}`} className="block">
      <Card className={`group cursor-pointer overflow-hidden border-border/50 shadow-sm hover:shadow-tropical transition-all duration-300 hover:-translate-y-1 active:scale-[0.98] touch-manipulation ${isCompact ? 'shadow-none hover:shadow-sm' : ''}`}>
        <div className={`relative overflow-hidden bg-muted ${isCompact ? 'aspect-[4/5]' : 'aspect-square'}`}>
          {/* Skeleton loader - only for lazy images while loading */}
          {!eager && !imageLoaded && imageUrl && <Skeleton className="absolute inset-0" />}
          
          {/* Product image - instant display, no fade animation */}
          {imageUrl && (
            <img 
              src={imageError ? "/images/proximamente.svg" : imageUrl} 
              alt={title}
              width={400}
              height={isCompact ? 500 : 400}
              loading={eager ? "eager" : "lazy"}
              fetchPriority={fetchPriority || (eager ? "high" : "auto")}
              decoding={eager ? "sync" : "async"}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setImageLoaded(true);
              }}
              className="absolute inset-0 object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
            />
          )}
          
          {/* Placeholder icon when no image URL exists */}
          {!imageUrl && (
            <img 
              src="/images/proximamente.svg"
              alt="Próximamente"
              width={400}
              height={isCompact ? 500 : 400}
              loading={eager ? "eager" : "lazy"}
              className="absolute inset-0 object-cover w-full h-full"
            />
          )}
          
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          
          {/* Top left badges */}
          <div className={`absolute flex flex-col gap-1 ${isCompact ? 'top-1.5 left-1.5' : 'top-2 left-2 md:top-3 md:left-3'}`}>
            {visibilityBadge && (
              <Badge 
                className={`shadow-md text-white ${isCompact ? 'text-[10px] px-1.5 py-0' : 'text-xs'}`}
                style={{ backgroundColor: visibilityBadgeColor || '#10b981' }}
              >
                <Sparkles className={`${isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3 mr-0.5'}`} />
                {!isCompact && visibilityBadge}
              </Badge>
            )}
            {isNew && !hasActiveDiscount && !visibilityBadge && (
              <Badge className={`bg-primary text-primary-foreground shadow-md animate-pulse ${isCompact ? 'text-[10px] px-1.5 py-0' : 'text-xs'}`}>
                <Sparkles className={`mr-0.5 ${isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3 mr-1'}`} />
                {!isCompact && 'Nuevo'}
              </Badge>
            )}
            {hasActiveDiscount && (
              <Badge className={`bg-destructive text-destructive-foreground shadow-md ${isCompact ? 'text-[10px] px-1.5 py-0' : 'text-xs'}`}>
                -{discountPercentage}%
              </Badge>
            )}
          </div>
          
          {/* Product type badges - Simplified on mobile, hidden in compact */}
          {!isCompact && (
            <div className="absolute bottom-2 left-2 md:bottom-3 md:left-3 flex gap-1.5 md:gap-2">
              {isSample && (
                <Badge variant="outline" className="bg-background/90 backdrop-blur-sm border-muted-foreground/50 text-muted-foreground shadow-sm text-[10px] md:text-xs px-1.5 md:px-2">
                  <span className="hidden sm:inline">Ejemplo</span>
                  <span className="sm:hidden">Ej.</span>
                </Badge>
              )}
              {isDigital && (
                <Badge className="bg-primary text-primary-foreground shadow-sm text-[10px] md:text-xs px-1.5 md:px-2">
                  <Download className="h-3 w-3 mr-0.5 md:mr-1" />
                  <span className="hidden sm:inline">Digital</span>
                </Badge>
              )}
              {isCustomizable && (
                <Badge className="bg-primary text-primary-foreground shadow-sm text-[10px] md:text-xs px-1.5 md:px-2">
                  <Palette className="h-3 w-3 mr-0.5 md:mr-1" />
                  <span className="hidden sm:inline">Personalizable</span>
                </Badge>
              )}
              {shippingCostUs != null && !isPrintOnDemand && (
                <Badge className="bg-blue-600 text-white shadow-sm text-[10px] md:text-xs px-1.5 md:px-2">
                  <Globe className="h-3 w-3 mr-0.5 md:mr-1" />
                  <span className="hidden sm:inline">Envío EE.UU.</span>
                  <span className="sm:hidden">🇺🇸</span>
                </Badge>
              )}
            </div>
          )}

          {/* Gastronomía zone badge */}
          {categoryMetadata?.zona_entrega && !isCompact && (
            <div className="absolute top-2 right-2 md:top-3 md:right-3">
              <Badge className="bg-orange-500 text-white shadow-sm text-[10px] md:text-xs px-1.5 md:px-2">
                <MapPin className="h-3 w-3 mr-0.5" />
                <span className="hidden sm:inline">{categoryMetadata.zona_entrega}</span>
                <span className="sm:hidden">📍</span>
              </Badge>
            </div>
          )}
          
        </div>
        
        <CardContent className={isCompact ? 'p-2' : 'p-3 md:p-4'}>
          <h3 className={`font-medium text-foreground group-hover:text-primary transition-colors leading-snug ${isCompact ? 'text-xs line-clamp-1 mb-1' : 'text-sm md:text-base line-clamp-2 mb-1.5 md:mb-2'}`}>
            {title}
          </h3>
          
          {/* Rating - Hidden in compact mode */}
          {!isCompact && (
            <div className="flex items-center gap-1 md:gap-1.5 mb-1.5 md:mb-2">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`h-3 w-3 md:h-3.5 md:w-3.5 ${
                      i < Math.floor(rating) 
                        ? "text-accent fill-accent" 
                        : "text-muted-foreground/30"
                    }`}
                  />
                ))}
              </div>
              {reviewCount > 0 && (
                <span className="text-[10px] md:text-xs text-muted-foreground">({reviewCount})</span>
              )}
            </div>
          )}
          
          {/* Tags - only shown in non-compact variant */}
          {!isCompact && tags && tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {tags.slice(0, 2).map((tag, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className="px-1.5 py-0 border-border/30 text-muted-foreground cursor-pointer hover:bg-primary/10 hover:border-primary hover:text-primary transition-colors text-[10px] md:text-xs h-5"
                  onClick={(e) => handleTagClick(e, tag)}
                >
                  #{tag}
                </Badge>
              ))}
              {tags.length > 2 && (
                <span className="text-muted-foreground self-center text-[10px]">
                  +{tags.length - 2}
                </span>
              )}
            </div>
          )}
          
          {/* Price - Responsive sizing */}
          <div className={`flex items-baseline gap-1.5 flex-wrap ${isCompact ? '' : 'md:gap-2'}`}>
            {hasActiveDiscount && discountPrice ? (
              <>
                <span className={`font-bold text-destructive ${isCompact ? 'text-sm' : 'text-lg md:text-xl'}`}>
                  ₡{discountPrice.toLocaleString('es-CR')}
                </span>
                <span className={`text-muted-foreground line-through ${isCompact ? 'text-[10px]' : 'text-xs md:text-sm'}`}>
                  ₡{price.toLocaleString('es-CR')}
                </span>
              </>
            ) : (
              <span className={`font-bold text-foreground ${isCompact ? 'text-sm' : 'text-lg md:text-xl'}`}>
                ₡{price.toLocaleString('es-CR')}
              </span>
            )}
          </div>

          {/* Store Link - Compact version with fixed height for consistency */}
          {isCompact && (
            <div className="min-h-[16px] mt-1.5">
              {sellerId && storeName ? (
                <Link 
                  to={storeLink}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-primary transition-colors group/store"
                >
                  {storeLogo ? (
                    <img src={storeLogo} alt={storeName} className="w-3.5 h-3.5 rounded-full object-cover" />
                  ) : (
                    <Store className="h-3 w-3" />
                  )}
                  <span className="truncate">{storeName}</span>
                  <ChevronRight className="h-2.5 w-2.5 opacity-0 group-hover/store:opacity-100 transition-opacity" />
                </Link>
              ) : null}
            </div>
          )}

          {/* Store Link - Full version (non-compact) */}
          {!isCompact && sellerId && storeName && (
            <Link 
              to={storeLink}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-2 mt-2 p-2 rounded-lg bg-muted/50 hover:bg-primary/10 transition-colors group/store"
            >
              {storeLogo ? (
                <img src={storeLogo} alt={storeName} className="w-5 h-5 rounded-full object-cover" />
              ) : (
                <Store className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-xs text-muted-foreground truncate flex-1">
                {storeName}
              </span>
              <ChevronRight className="h-3 w-3 text-muted-foreground group-hover/store:text-primary group-hover/store:translate-x-0.5 transition-all" />
            </Link>
          )}

          {/* Buy + WhatsApp buttons */}
          <div className={`flex items-stretch gap-1.5 ${isCompact ? 'mt-1.5' : 'mt-2.5'}`}>
            <button
              className={`group/buy flex-1 rounded-[3px] border bg-transparent uppercase tracking-[0.18em] font-medium flex items-center justify-center gap-1.5 transition-colors duration-200 ${
                isCompact ? 'text-[9px] h-7' : 'text-[10px] h-9'
              }`}
              style={{
                borderColor: themeColor || 'hsl(var(--primary))',
                backgroundColor: buyHover ? (themeColor || 'hsl(var(--primary))') : 'transparent',
                color: buyHover ? 'hsl(var(--primary-foreground))' : (themeColor || 'hsl(var(--primary))'),
              }}
              onMouseEnter={() => setBuyHover(true)}
              onMouseLeave={() => setBuyHover(false)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/productos/${id}`);
              }}
            >
              Comprar
              <ArrowRight
                className={`${isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3'} transition-transform duration-200 group-hover/buy:translate-x-0.5`}
              />
            </button>
            {storeWhatsapp && (
              <a
                href={buildWhatsappUrl(
                  storeWhatsapp,
                  buildProductInquiryMessage(title, `${window.location.origin}/productos/${id}`)
                )}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Consultar por WhatsApp"
                title="Consultar por WhatsApp"
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => setWaHover(true)}
                onMouseLeave={() => setWaHover(false)}
                className={`shrink-0 rounded-[3px] border flex items-center justify-center transition-colors duration-200 ${
                  isCompact ? 'h-7 w-7' : 'h-9 w-9'
                }`}
                style={{
                  borderColor: '#25D366',
                  backgroundColor: waHover ? '#25D366' : 'transparent',
                  color: waHover ? '#ffffff' : '#25D366',
                }}
              >

                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className={isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5'}
                  aria-hidden="true"
                >
                  <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.5 0 .17 5.34.17 11.9c0 2.1.55 4.14 1.6 5.95L0 24l6.32-1.66a11.86 11.86 0 0 0 5.74 1.46h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.17-3.44-8.42ZM12.07 21.6h-.01a9.7 9.7 0 0 1-4.95-1.36l-.36-.21-3.75.98 1-3.65-.23-.37a9.7 9.7 0 0 1-1.49-5.18c0-5.36 4.36-9.72 9.73-9.72 2.6 0 5.04 1.01 6.88 2.85a9.66 9.66 0 0 1 2.85 6.88c0 5.36-4.37 9.78-9.67 9.78Zm5.6-7.27c-.31-.16-1.81-.9-2.09-1-.28-.1-.49-.16-.7.16-.2.31-.79 1-.97 1.2-.18.2-.36.23-.67.08a8.27 8.27 0 0 1-2.43-1.5 9.16 9.16 0 0 1-1.69-2.1c-.18-.31-.02-.48.13-.63.13-.13.31-.36.46-.54.16-.18.2-.31.31-.51.1-.21.05-.39-.03-.54-.08-.16-.7-1.68-.96-2.31-.25-.6-.5-.52-.7-.53l-.6-.01c-.21 0-.54.08-.83.39-.28.31-1.08 1.06-1.08 2.58s1.11 3 1.27 3.21c.16.21 2.18 3.34 5.29 4.68.74.32 1.32.51 1.77.65.74.23 1.41.2 1.95.12.6-.09 1.81-.74 2.07-1.45.26-.71.26-1.31.18-1.45-.08-.13-.28-.21-.59-.36Z" />
                </svg>
              </a>
            )}
          </div>


        </CardContent>
      </Card>
    </Link>
  );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
